import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Check, FileText, Search } from 'lucide-react';
import { listContext } from '@/services/knowledgeApi';
import type { ManualContextNote } from '@Types/knowledge';
import {
  isContextSelectable,
  isLargeKnowledgeDocument,
  knowledgeLibraryBlurb,
} from '@/utils/knowledgeStatus';

interface ContextPickerProps {
  anchorRef: React.RefObject<HTMLElement>;
  activeIds: Set<string>;
  onToggle: (note: ManualContextNote) => void;
  onClose: () => void;
  onOpenLibrary: () => void;
}

const PICKER_WIDTH = 320;
const VIEWPORT_MARGIN = 8;

export const ContextPicker = ({
  anchorRef,
  activeIds,
  onToggle,
  onClose,
  onOpenLibrary,
}: ContextPickerProps) => {
  const [notes, setNotes] = useState<ManualContextNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    listContext()
      .then(res => {
        if (!cancelled) {
          setNotes(
            [...res.tenant, ...res.user].filter(
              n => n.apply_mode !== 'always' && isContextSelectable(n),
            ),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setNotes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popoverHeight = containerRef.current?.offsetHeight ?? 320;
      const top = rect.top - popoverHeight - 8;
      let left = rect.left;
      const maxLeft = window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      setPosition({
        top: Math.max(VIEWPORT_MARGIN, top),
        left,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorRef, loading, notes.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !anchorRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [anchorRef, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter(
      n =>
        n.name.toLowerCase().includes(q) ||
        n.description.toLowerCase().includes(q),
    );
  }, [notes, query]);

  if (!position) return null;

  return createPortal(
    <div
      ref={containerRef}
      className="fixed z-50 w-80 rounded-xl border border-[#E4E9EC] bg-white shadow-[0_12px_40px_rgba(16,24,40,.12)] overflow-hidden"
      style={{ top: position.top, left: position.left }}>
      <div className="px-3 py-2.5 border-b border-[#EEF2F4] bg-gradient-to-b from-[#FAFBFC] to-white space-y-1.5">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-[#768D95]" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search knowledge..."
            className="flex-1 text-sm outline-none bg-transparent text-[#252F33]"
          />
        </div>
        <p className="text-[11px] text-[#768D95] pl-6">
          Attach sources or notes for this message (optional)
          {activeIds.size > 0 ? ` · ${activeIds.size} selected` : ''}
        </p>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {loading ? (
          <p className="px-3 py-4 text-sm text-[#768D95]">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-4 text-sm text-[#768D95]">No knowledge yet.</p>
        ) : (
          filtered.map(note => {
            const on = activeIds.has(note.context_id);
            return (
              <button
                key={note.context_id}
                type="button"
                onClick={() => onToggle(note)}
                className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${
                  on ? 'bg-[#E8F1F5]/60' : 'hover:bg-[#FAFBFC]'
                }`}>
                {note.item_type === 'document' ? (
                  <FileText className="h-4 w-4 mt-0.5 text-[#007CA0] flex-shrink-0" />
                ) : (
                  <BookOpen className="h-4 w-4 mt-0.5 text-[#007CA0] flex-shrink-0" />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-[#252F33] truncate">
                    {note.name}
                  </span>
                  <span className="block text-xs text-[#768D95] line-clamp-2">
                    {knowledgeLibraryBlurb(note) ||
                      (isLargeKnowledgeDocument(note) && note.chunk_count
                        ? `${note.chunk_count} passages indexed`
                        : note.description)}
                  </span>
                </span>
                {on && (
                  <Check className="h-4 w-4 text-[#007CA0] flex-shrink-0" />
                )}
              </button>
            );
          })
        )}
      </div>
      <div className="border-t border-slate-100 px-3 py-2">
        <button
          type="button"
          onClick={() => {
            onClose();
            onOpenLibrary();
          }}
          className="text-sm text-[#006EAF] hover:underline">
          Manage knowledge
        </button>
      </div>
    </div>,
    document.body,
  );
};
